import { z } from 'zod';
import { MAX_DAILY_QUARTER_HOURS } from '@obraplan/core/domain/calendar/constants';
import { MIN_JUSTIFICATION_LENGTH } from '@obraplan/core/domain/planning/over-capacity-policy';
import { MAX_NON_CONFORMITY_PLACES } from '@obraplan/core/domain/quality/non-conformity-place';
import { businessDateSchema, idSchema, nonEmptyString, optionalTrimmed } from './common';

/**
 * Contratos do planejamento semanal.
 *
 * As horas chegam da tela em **quartos de hora**, não em decimal: o campo já trabalha em
 * incrementos de 15 minutos, e converter na borda evita que um `0.1` digitado à mão vire
 * um arredondamento silencioso lá dentro. A validação de granularidade continua no
 * domínio — aqui é só a forma.
 */

const isoYearSchema = z.number().int().min(2000).max(2199);
const isoWeekSchema = z.number().int().min(1).max(53);

export const weekRefSchema = z.object({
  projectId: idSchema,
  isoYear: isoYearSchema,
  isoWeek: isoWeekSchema,
});
export type WeekRefInput = z.infer<typeof weekRefSchema>;

export const quarterHoursSchema = z
  .number()
  .int('As horas devem ser múltiplas de 15 minutos.')
  .positive('As horas alocadas devem ser maiores que zero.')
  .max(MAX_DAILY_QUARTER_HOURS, 'Uma alocação de um dia não pode passar de 24 horas.');

export const justificationSchema = z
  .string()
  .trim()
  .min(
    MIN_JUSTIFICATION_LENGTH,
    `A justificativa precisa de pelo menos ${String(MIN_JUSTIFICATION_LENGTH)} caracteres.`,
  )
  .max(500, 'A justificativa deve ter no máximo 500 caracteres.');

export const createWeeklyAllocationSchema = z.object({
  projectId: idSchema,
  isoYear: isoYearSchema,
  isoWeek: isoWeekSchema,
  resourceId: idSchema,
  activityId: idSchema,
  /**
   * O serviço do dia — o que a obra chama de atividade (ADR-135).
   *
   * Opcional no contrato e obrigatório na tela: o planejamento sempre o manda, e quem o
   * omite é o lançamento do RDO numa frente sem serviço cadastrado (ADR-133). Exigi-lo
   * aqui fecharia esse caminho, que existe porque nem toda frente tem serviço.
   */
  serviceId: idSchema.optional(),
  /**
   * Os dias trabalhados, com as horas de cada um.
   *
   * Uma lista, e não um dia só, porque é assim que o serviço é montado: "alvenaria do 2º,
   * 4h na segunda e 4h na terça". Os dias entram na **mesma transação** — se um deles
   * estourar a jornada, nenhum é gravado, e quem planeja corrige antes de qualquer
   * registro parcial existir.
   */
  days: z
    .array(
      z.object({
        date: businessDateSchema,
        quarterHours: quarterHoursSchema,
        /**
         * Quanto se espera produzir neste dia, na unidade da atividade.
         *
         * Sem casa decimal fixa aqui: quem arredonda é a unidade do catálogo (RN-020),
         * porque `m³` tem três casas e `un` não tem nenhuma. O contrato só recusa o que
         * não é número ou é negativo.
         */
        quantity: z.number().nonnegative('A quantidade não pode ser negativa.').optional(),
      }),
    )
    .min(1, 'Informe pelo menos um dia com horas.')
    .max(7, 'Uma semana tem no máximo sete dias.'),
  /** Presente = o usuário viu o excesso e escreveu o porquê (RN-045). */
  justification: justificationSchema.optional(),
  /** Presente = o usuário confirmou alocar em atividade impedida (RN-085). */
  acknowledgeRestriction: z.boolean().optional(),
  notes: optionalTrimmed(500),
});
export type CreateWeeklyAllocationInput = z.infer<typeof createWeeklyAllocationSchema>;

export const updateWeeklyAllocationSchema = z.object({
  allocationId: idSchema,
  quarterHours: quarterHoursSchema,
  /** Versão lida pela tela. Divergente = outra aba gravou antes (bloqueio otimista). */
  expectedVersion: z.number().int().positive(),
  justification: justificationSchema.optional(),
  notes: optionalTrimmed(500),
  /** `null` limpa a meta do dia; ausente deixa como está. */
  plannedQuantity: z
    .number()
    .nonnegative('A quantidade não pode ser negativa.')
    .nullable()
    .optional(),
});

export const deleteWeeklyAllocationSchema = z.object({
  allocationId: idSchema,
  expectedVersion: z.number().int().positive(),
  /**
   * Motivo da exclusão.
   *
   * Excluir é alterar (RN-073) e entra no histórico. O motivo é opcional — e é o mesmo
   * campo que a exceção de capacidade usa: quem já escreveu o porquê não escreve um
   * segundo texto.
   */
  justification: justificationSchema.optional(),
});

export const changeWeeklyPlanStatusSchema = z.object({
  projectId: idSchema,
  isoYear: isoYearSchema,
  isoWeek: isoWeekSchema,
  status: z.enum(['DRAFT', 'PUBLISHED', 'CLOSED']),
});

export const copyPreviousWeekSchema = z.object({
  projectId: idSchema,
  isoYear: isoYearSchema,
  isoWeek: isoWeekSchema,
  /** Sem gravar: devolve a pré-visualização do que seria copiado. */
  preview: z.boolean().optional(),
});

/**
 * Proposta automática a partir do cronograma.
 *
 * `resourceIds` ausente = aplicar a proposta inteira; presente = apenas esses recursos,
 * que é o que a revisão por funcionário envia. A proposta é **recalculada** na aplicação
 * e nunca chega pronta da tela: entre ver e aplicar, alguém pode ter gravado horas na
 * mesma semana, e gravar números de trinta segundos atrás estouraria a jornada de quem
 * acabou de receber serviço.
 */
export const autoAllocateFromScheduleSchema = z.object({
  projectId: idSchema,
  isoYear: isoYearSchema,
  isoWeek: isoWeekSchema,
  resourceIds: z.array(idSchema).max(500).optional(),
});
export type AutoAllocateFromScheduleInput = z.infer<typeof autoAllocateFromScheduleSchema>;

export const dailyScheduleSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
});

/**
 * Mover a alocação de um dia para outro.
 *
 * Leva `expectedVersion` pelo mesmo motivo de alterar e excluir: duas abas na mesma folha
 * do dia não podem arrastar a mesma linha para lugares diferentes sem ninguém perceber.
 */
export const moveAllocationSchema = z.object({
  allocationId: idSchema,
  date: businessDateSchema,
  expectedVersion: z.number().int().positive(),
  /** Mover é alterar e entra no histórico; o motivo só é exigido na hora extra (RN-292). */
  justification: justificationSchema.optional(),
});

export const inspectionListSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
});

export const confirmInspectionSchema = z.object({
  projectId: idSchema,
  resourceId: idSchema,
  date: businessDateSchema,
});

export const registerAbsenceSchema = z.object({
  projectId: idSchema,
  resourceId: idSchema,
  date: businessDateSchema,
  reason: nonEmptyString('Motivo da ausência', 300),
});

/**
 * Ajuste pela conferência.
 *
 * As horas seguem a mesma regra da alocação: **maiores que zero**. Aceitar `0` aqui só
 * adiaria a recusa até o domínio (`parseAllocationHours`), com uma mensagem que não
 * explica o que fazer. Quem não trabalhou no dia inteiro tem "registrar ausência"; a
 * frente que não aconteceu sai pelo "Tirar" do dia da pessoa (RN-631), que é exclusão
 * registrada no histórico (RN-073).
 */
const adjustedQuarterHoursSchema = z
  .number()
  .int('As horas devem ser múltiplas de 15 minutos.')
  .positive(
    'As horas executadas devem ser maiores que zero. Para um dia sem trabalho, registre a ausência; para uma frente que não aconteceu, use "Tirar" na frente.',
  )
  .max(MAX_DAILY_QUARTER_HOURS, 'Um dia não passa de 24 horas.');

export const adjustInspectionSchema = z.object({
  allocationId: idSchema,
  quarterHours: adjustedQuarterHoursSchema,
  expectedVersion: z.number().int().positive(),
  /**
   * As outras frentes **do mesmo dia da mesma pessoa**, ajustadas na mesma confirmação.
   *
   * O diálogo da conferência ajusta o dia inteiro de uma vez, e por isso elas vêm juntas:
   * ajustar encerra a conferência (RN-104), e a segunda frente, numa chamada à parte,
   * encontraria o dia já encerrado (RN-106). E a hora extra é do total **final** do dia
   * (RN-292) — gravar frente a frente conferiria totais do meio do caminho, que ninguém
   * digitou, e exigiria motivo (ou a permissão) por um excesso que o dia não tem.
   */
  otherAllocations: z
    .array(
      z.object({
        allocationId: idSchema,
        quarterHours: adjustedQuarterHoursSchema,
        expectedVersion: z.number().int().positive(),
      }),
    )
    .max(20, 'Um dia não tem tantas frentes assim.')
    .optional(),
  /** Obrigatória só na hora extra: o total do dia passou da jornada (RN-291, RN-292). */
  justification: justificationSchema.optional(),
  notes: optionalTrimmed(500),
  /**
   * Quanto foi produzido de fato.
   *
   * Não é alteração de planejamento e não entra na sequência do dia: quem escreve o que saiu não
   * mudou o plano, disse o que aconteceu com ele. `null` limpa a medição.
   */
  executedQuantity: z
    .number()
    .nonnegative('A quantidade não pode ser negativa.')
    .nullable()
    .optional(),
});

/**
 * Tirar a frente do dia da pessoa (RN-631): a que não aconteceu, lançada pelo RDO ou planejada.
 *
 * É a exclusão da alocação, pela conferência — entra no histórico do dia como a do planejamento
 * (RN-073), e o motivo é opcional como lá.
 */
export const removeInspectionAllocationSchema = z.object({
  allocationId: idSchema,
  expectedVersion: z.number().int().positive(),
  justification: justificationSchema.optional(),
});

/**
 * A medição do dia — o que cada frente realmente produziu.
 *
 * Vem em lote porque é assim que se mede: o conferente olha o dia inteiro daquela pessoa
 * de uma vez, não uma frente por vez. `null` limpa a medição de uma frente sem mexer nas
 * outras.
 */
/**
 * A verificação de qualidade que acompanha uma escrita (Fase 19, RN-218).
 *
 * Uma por `(frente, serviço)`: o serviço declarado e as fichas respondidas dele. Vem no
 * mesmo envelope da medição — e, desde a Fase 20, do trabalho lançado pelo RDO — porque
 * é a mesma transação: a quantidade só grava se a verificação passar. `default([])`
 * mantém válida a chamada de quem só mede frente sem serviço.
 */
export const verificationsSchema = z
  .array(
    z.object({
      activityId: idSchema,
      serviceId: idSchema,
      checklists: z
        .array(
          z.object({
            checklistId: idSchema,
            answers: z
              .array(
                z.object({
                  itemId: idSchema,
                  result: z.enum(['APPROVED', 'REJECTED', 'NOT_APPLICABLE']),
                  nonConformity: z
                    .object({
                      description: z
                        .string()
                        .trim()
                        .max(500, 'A descrição da não conformidade é longa demais.'),
                      // Obrigatória, mas conferida pelo domínio: quem chega sem ela recebe a
                      // mensagem da regra, com o item, e não um "campo inválido" genérico.
                      solution: z
                        .string()
                        .trim()
                        .max(500, 'A solução aplicada é longa demais.')
                        .optional(),
                      // A quantidade não conforme (Fase 37, RN-473). As casas e o teto são do
                      // caso de uso, que sabe a unidade do serviço e o que a medição mediu.
                      quantity: z
                        .number()
                        .finite()
                        .positive('A quantidade não conforme precisa ser maior que zero.')
                        .optional(),
                      // Os cômodos e as peças do dia onde ela foi encontrada (Fase 37, D9). O
                      // caso de uso confere que são do dia (RN-517).
                      places: z
                        .object({
                          pieces: z
                            .array(z.string().trim().min(1).max(120))
                            .max(MAX_NON_CONFORMITY_PLACES, 'Peças demais numa não conformidade.'),
                          rooms: z
                            .array(z.object({ positionId: idSchema, roomId: idSchema }))
                            .max(
                              MAX_NON_CONFORMITY_PLACES,
                              'Cômodos demais numa não conformidade.',
                            ),
                        })
                        .optional(),
                    })
                    .optional(),
                }),
              )
              .max(60),
          }),
        )
        .max(10),
    }),
  )
  .max(40)
  .default([]);

export type VerificationsInput = z.infer<typeof verificationsSchema>;

/**
 * A NC de uma medição num item, depois de gravada (Fase 37). É por ela que a tela sobe as
 * fotos que foram escolhidas no modal antes de a NC ter número (RN-479).
 */
export interface NonConformityRef {
  readonly activityId: string;
  readonly serviceId: string;
  readonly checklistId: string;
  readonly itemId: string;
  readonly nonConformityId: string;
  readonly number: number;
}

export const recordDayMeasurementSchema = z.object({
  inspectionId: idSchema,
  measurements: z
    .array(
      z.object({
        allocationId: idSchema,
        executedQuantity: z.number().nonnegative('A quantidade não pode ser negativa.').nullable(),
      }),
    )
    .max(20, 'Um dia não tem tantas frentes assim.'),
  /**
   * A verificação de qualidade que acompanha a medição (Fase 19, RN-218).
   *
   * Uma por `(frente, serviço)`: o serviço declarado e as fichas respondidas dele. Vem no
   * mesmo envelope porque é a mesma transação — a quantidade só grava se a verificação
   * passar, e uma segunda chamada deixaria a quantidade gravada com a verificação recusada.
   * `default([])` mantém válida a chamada de quem só mede frente sem serviço.
   */
  verifications: verificationsSchema,
});

export const reopenInspectionSchema = z.object({
  inspectionId: idSchema,
});

/**
 * O trabalho lançado pelo RDO (Fase 20, RN-224).
 *
 * Uma pessoa, uma frente, um dia: as horas gastas, a quantidade (se a frente é medida) e
 * a verificação de qualidade que a Fase 19 exige quando a disciplina tem serviço. Vira
 * alocação do dia e conferência na mesma transação (ADR-131). `justification` é a da
 * exceção de capacidade (RN-045), quando as horas passam da jornada.
 */
export const recordReportedWorkSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  resourceId: idSchema,
  activityId: idSchema,
  /**
   * O serviço lançado — o que o canteiro chama de atividade.
   *
   * Opcional porque a frente pode não ter serviço cadastrado (ADR-133); presente, é dele
   * que sai a unidade da medição (RN-232).
   */
  serviceId: idSchema.optional(),
  quarterHours: quarterHoursSchema,
  executedQuantity: z.number().nonnegative('A quantidade não pode ser negativa.').optional(),
  verifications: verificationsSchema,
  justification: justificationSchema.optional(),
  /**
   * A frente que este lançamento corrige (RN-645): a atividade errada sai do dia na mesma
   * transação em que a certa entra — é o "Tirar" dela (RN-631), com a conferência reaberta antes.
   */
  replaces: z
    .object({ allocationId: idSchema, expectedVersion: z.number().int().positive() })
    .optional(),
});

/**
 * A qualidade do dia (Fase 19), com frentes extras a pedido (Fase 20).
 *
 * `activityIds` inclui na leitura frentes que ainda não têm alocação no dia — é o que o
 * RDO precisa para abrir a checklist de uma frente **antes** de lançá-la.
 */
export const dayQualitySchema = inspectionListSchema.extend({
  activityIds: z.array(idSchema).max(20).optional(),
});

/**
 * Relatório Diário de Obra (Fase 18).
 *
 * As três entradas são `(obra, dia)`, e não o id do relatório: quem chama é a tela do dia,
 * que sabe a data antes de saber se existe documento nela. Resolver a chave natural aqui
 * dentro poupa a tela de uma leitura só para descobrir um id.
 */
export const dailyReportSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
});

/**
 * O tempo e a jornada do dia (fatia 2).
 *
 * A tela manda os três turnos, inclusive os vazios — é mais simples para ela mandar o
 * formulário inteiro do que decidir o que omitir. Quem separa o relatado do vazio é
 * `parseShifts`, no domínio (RN-211), que é onde a regra mora.
 *
 * `workable` tem padrão verdadeiro porque o dia comum é o que deu para trabalhar, e um
 * formulário que exige marcar "sim" três vezes por dia ensina a marcar sem ler.
 */
export const dailyReportWeatherSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  shifts: z
    .array(
      z.object({
        shift: z.enum(['MORNING', 'AFTERNOON', 'NIGHT']),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        condition: z.enum(['CLEAR', 'CLOUDY', 'RAINY', 'STORM']).optional(),
        workable: z.boolean().default(true),
      }),
    )
    .max(3, 'O dia tem três turnos.'),
});

/**
 * As tarefas avulsas do dia (RN-213).
 *
 * A tela manda a tabela inteira, inclusive as linhas em branco que a pessoa não
 * preencheu. Quem descarta o vazio é `parseReportTasks`, no domínio, pelo mesmo motivo do
 * turno vazio: campo não preenchido não é erro de quem preenche.
 */
export const dailyReportTasksSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  tasks: z
    .array(
      z.object({
        description: z.string().max(200, 'A descrição da tarefa é longa demais.'),
        notes: z.string().max(500, 'A observação da tarefa é longa demais.').optional(),
        // Fase 20 — o que o RDO escreve ao lado da avulsa. Texto do dia: quem valida
        // limites e múltiplos é `parseReportTasks`, no domínio.
        workers: z.array(z.string().max(80)).max(10).optional(),
        quantity: z.number().optional(),
        unit: z.string().max(20).optional(),
        quarterHours: z.number().optional(),
      }),
    )
    .max(50, 'São tarefas avulsas demais para um dia.'),
});

/**
 * O efetivo corrigido por função (RN-212).
 *
 * Aceita **zero**: "hoje não tinha pedreiro" é uma correção, e não um campo em branco.
 * Quem descarta a linha sem função é `parseCrewCounts`, no domínio.
 */
export const dailyReportCrewSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  crew: z
    .array(
      z.object({
        roleName: z.string().max(120, 'O nome da função é longo demais.'),
        quantity: z
          .number()
          .int('O efetivo é um número inteiro de pessoas.')
          .min(0, 'O efetivo não pode ser negativo.')
          .max(999, 'Este efetivo é grande demais para um dia de obra.'),
      }),
    )
    .max(60, 'São funções demais para um dia de obra.'),
});

/**
 * O equipamento do dia (ADR-123).
 *
 * A quantidade começa em **um**: equipamento que não foi usado sai da lista, e não fica
 * nela com zero. É o que faz a herança do dia anterior ser corrigível em vez de acumular.
 */
export const dailyReportEquipmentSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  equipment: z
    .array(
      z.object({
        code: z.string().max(40, 'O código do equipamento é longo demais.').optional(),
        description: z.string().max(200, 'A descrição do equipamento é longa demais.'),
        status: z.enum(['IN_USE', 'IDLE', 'MAINTENANCE']),
        quantity: z
          .number()
          .int('A quantidade de equipamento é um número inteiro.')
          .min(1, 'Equipamento que não foi usado sai da lista.')
          .max(999, 'Esta quantidade é grande demais para um dia de obra.'),
      }),
    )
    .max(80, 'São equipamentos demais para um dia de obra.'),
});

export const dailyReportNotesSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  /** Texto vazio apaga a observação — ausência e "escreveram nada" são a mesma coisa. */
  notes: z.string().max(2000, 'A observação do dia é longa demais.'),
});

export const resourceAvailabilitySchema = z.object({
  projectId: idSchema,
  isoYear: isoYearSchema,
  isoWeek: isoWeekSchema,
});
